<template>
    <!-- Confirmation dialog: business logic (Promise-based OpenModal) lives here;
         the overlay/box/motion shell is delegated to BaseModal. During processing
         the modal is non-dismissible so it can't be closed mid-callback. -->
    <BaseModal
        v-model:show="visible"
        :title="title"
        :closable="!processing"
        :dismissible="!processing"
        @close="OnCancel"
    >
        <div class="confirm-modal__body">
            <p class="confirm-modal__text">{{ message }}</p>
            <p v-if="definitive" class="confirm-modal__text confirm-modal__text--warn">
                Esta acción será definitiva y no podrá cancelarse
            </p>

            <div class="confirm-modal__actions">
                <button
                    v-if="showCancelButton"
                    type="button"
                    class="confirm-modal__btn confirm-modal__btn--ghost"
                    :disabled="processing"
                    @click="OnCancel"
                >
                    <i class="bi bi-x-circle"></i>
                    Cancelar
                </button>
                <button
                    type="button"
                    class="confirm-modal__btn confirm-modal__btn--confirm"
                    :disabled="processing"
                    @click="OnConfirm"
                >
                    <i class="bi bi-check-circle"></i>
                    Confirmar
                </button>
            </div>
        </div>
    </BaseModal>
</template>

<script>
import BaseModal from '@/components/Generic/BaseModal.vue';

export default {
    name: 'ConfirmationModal',

    components: {
        BaseModal
    },

    data(){
        return {
            visible: false,
            title: 'Confirmar acción',
            message: '',
            definitive: false,
            showCancelButton: true,
            confirmation: null,
            value: null,
            resolver: null,
            processing: false
        }
    },

    methods: {
        // Kept signature-compatible with the previous SweetAlert-based version.
        // Resolves true when the confirmation callback succeeds, false when the
        // user cancels/closes/clicks outside. If the confirmation callback
        // rejects, the modal closes without resolving (same as before).
        OpenModal(message, definitive, confirmation, value, showCancelButton = true, title = 'Confirmar acción'){
            this.message = message;
            this.definitive = definitive;
            this.confirmation = confirmation;
            this.value = value;
            this.showCancelButton = showCancelButton;
            this.title = title;
            this.processing = false;
            this.visible = true;

            return new Promise((resolve) => {
                this.resolver = resolve;
            });
        },

        Close(){
            this.visible = false;
        },

        Resolve(result){
            if(this.resolver){
                const resolve = this.resolver;
                this.resolver = null;
                resolve(result);
            }
        },

        OnCancel(){
            if(this.processing)
                return;

            this.Close();
            this.Resolve(false);
        },

        OnConfirm(){
            if(this.processing)
                return;

            if(typeof this.confirmation !== 'function'){
                this.Close();
                this.Resolve(true);
                return;
            }

            this.processing = true;

            this.confirmation(this.value)
            .then(() => {
                this.Close();
                this.Resolve(true);
            })
            .catch(() => {
                this.Close();
                this.processing = false;
            });
        }
    }
}
</script>

<style scoped>
/* Only the confirmation-specific content styles live here; the overlay/box/
   header/motion shell is owned by BaseModal. */
.confirm-modal__body {
    padding: 14px 24px 24px;
}

/* Some parent modals (e.g. FileModal) force their own mobile box height via
   :deep(.baseModal__box) — a Vue scoped-CSS descendant selector that pierces
   child component boundaries and also lands on this ConfirmationModal's own
   nested BaseModal when it's rendered inside them, stretching this short
   dialog to that height too. Always stay content-sized regardless. */
:deep(.baseModal__box) {
    height: auto !important;
}

.confirm-modal__text {
    font-size: 14.5px;
    line-height: 1.6;
    color: #2b2740;
    margin: 0 0 14px;
}

.confirm-modal__text--warn {
    color: #6b6680;
}

.confirm-modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}

.confirm-modal__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 42px;
    padding: 0 18px;
    border-radius: 10px;
    border: none;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 14px;
    cursor: pointer;
    transition: filter 200ms ease, background-color 200ms ease, border-color 200ms ease, color 200ms ease;
}

.confirm-modal__btn:disabled {
    opacity: 0.6;
    cursor: default;
}

.confirm-modal__btn--confirm {
    background-color: #1f1f2c;
    color: #ffffff;
}

.confirm-modal__btn--confirm:not(:disabled):hover {
    filter: brightness(1.18);
}

.confirm-modal__btn--ghost {
    background-color: transparent;
    color: #6b6680;
    border: 1px solid #ece8f4;
}

.confirm-modal__btn--ghost:not(:disabled):hover {
    border-color: #d8d3e6;
    color: #1f1f2c;
}
</style>
